/**
 * Shared attachment-picking/upload logic for SupportTicketForm.vue and TicketThread.vue (design doc
 * §12.1). Each selected file is uploaded to our backend immediately (not deferred to submit time) so
 * upload failures are caught before the user finishes writing their ticket/reply. Files are chained
 * via existingToken so Zendesk groups them into ONE upload token per ticket/reply - the tokens array
 * sent to the server therefore always has at most one entry, not one per file (see
 * ZendeskRequestsClient.uploadFile's own doc comment for why chaining, not a token-per-file list, is
 * the correct grouping mechanism).
 */
export default {
  data() {
    return {
      attachments: [], // { file, name, status: 'uploading'|'done'|'error', error }
      attachmentGroupToken: null,
      maxAttachments: 5,
      maxAttachmentBytes: 15 * 1024 * 1024,
    };
  },
  computed: {
    attachmentTokens() {
      return this.attachmentGroupToken ? [this.attachmentGroupToken] : [];
    },
    hasUploadingAttachments() {
      return this.attachments.some(function (a) { return a.status === 'uploading'; });
    },
  },
  methods: {
    // Uploads must run one at a time, NOT in parallel: each subsequent file has to chain off the
    // group token returned by the previous upload's completion (see uploadAttachment below). Firing
    // them all at once meant every upload read attachmentGroupToken before any had completed, so
    // they all raced for the same (still-null) value and formed independent, ungrouped uploads -
    // only whichever one happened to finish first ever made it into the submitted tokens.
    async onAttachmentFilesSelected(event) {
      var me = this;
      var files = Array.prototype.slice.call((event.target && event.target.files) || []);
      event.target.value = '';
      var remainingSlots = me.maxAttachments - me.attachments.length;
      var toUpload = files.slice(0, Math.max(0, remainingSlots));
      for (var i = 0; i < toUpload.length; i++) {
        await me.uploadAttachment(toUpload[i]);
      }
    },
    uploadAttachment(file) {
      var me = this;
      if (file.size > me.maxAttachmentBytes) {
        me.attachments.push({ file: file, name: file.name, status: 'error', error: me.$t('messages.attachmentTooLarge') });
        return Promise.resolve();
      }
      me.attachments.push({ file: file, name: file.name, status: 'uploading', error: null });
      // splice (not in-place property mutation) so the status change is unambiguously reactive in
      // Vue 2 even if this entry somehow wasn't picked up by the array-push observer.
      var index = me.attachments.length - 1;
      return window.vcCommunicator.uploadSupportTicketAttachment(file, me.attachmentGroupToken)
        .then(function (result) {
          if (!me.attachmentGroupToken) {
            me.attachmentGroupToken = result.token;
          }
          me.attachments.splice(index, 1, { file: file, name: file.name, status: 'done', error: null });
        })
        .catch(function (error) {
          me.attachments.splice(index, 1, { file: file, name: file.name, status: 'error', error: '' + error });
        });
    },
    // Zendesk's chained upload group can't have a single file un-linked from it after the fact, so
    // removing any successfully-uploaded file means re-uploading the remaining ones from scratch
    // to form a fresh group token. Failed entries can just be dropped (they never joined the group).
    async removeAttachment(entry) {
      var me = this;
      var remainingDone = this.attachments.filter(function (a) {
        return a !== entry && a.status === 'done';
      }).map(function (a) { return a.file; });
      this.attachments = this.attachments.filter(function (a) {
        return a !== entry && a.status !== 'done';
      });
      this.attachmentGroupToken = null;
      for (var i = 0; i < remainingDone.length; i++) {
        await me.uploadAttachment(remainingDone[i]);
      }
    },
    resetAttachments() {
      this.attachments = [];
      this.attachmentGroupToken = null;
    },
  },
};
